import type { Game } from '../game/Game';
import { KnyttPoint } from '../engine/KnyttPoint';
import { Collectable, JuniValues } from '../engine/JuniValues';
import { parseIniInt } from '../engine/KnyttUtil';

// MapPanel.cs: SCALE 16 (non-detailed) → integer cell size 600/16 x 240/16.
const SCALE = 16;
const XSIZE = Math.floor(600 / SCALE); // 37
const YSIZE = Math.floor(240 / SCALE); // 15
const SCROLL_SPEED = 600; // px/s (MapPanel.cs)
const BORDER = 20;

const VISITED_COLOR = '#8080ff'; // (0.5, 0.5, 1)
const NOT_VISITED_COLOR = '#808080'; // (0.5, 0.5, 0.5) — revealed by the Map power
const CURRENT_BORDER = '#ff0000';

// MapPanel.cs GRADIENTS: per-gradient average colour (pixel (3,237) of each
// default Gradient<N>.png), brightened by makeBright at draw time.
const GRADIENTS = [
  0x6e3dab, 0x283c4c, 0xd4bbff, 0x474646, 0x060021, 0x006363, 0x61707e, 0xf3f3a8,
  0x11508b, 0x755038, 0x61707e, 0xebcbc3, 0x742c5c, 0x535985, 0x010101, 0xdff1f6,
  0x3d205a, 0xcdb0ee, 0xffd4c7, 0x156b61, 0x9ba6a8, 0x333334, 0xd8b13d, 0x12465d,
  0x1d1d1d, 0xc0aa71, 0xf64239, 0xfa0008, 0x2e2e2e, 0x082927, 0xfeffff, 0x4c9233,
  0xc08e8c, 0xfeffff, 0x4a4e55, 0x5ec39e, 0x00002a, 0x4f5076, 0x272733, 0xaacda3,
  0x658399, 0x728181, 0xa5aaba, 0x181c24, 0x000000, 0x6b0052, 0x005edb, 0xffffff,
  0x302522, 0xb2bdd5, 0x00005a, 0x3f2c27, 0x928f9e, 0xf7feba, 0x8f8b54, 0x414165,
  0x3a0065, 0x171063, 0x3c3c3c, 0x514e47, 0x008040, 0x0a018e, 0x1f2c4b, 0x00137f,
  0xdffddb, 0x7499ca, 0x1f1f1f, 0x402e34, 0x1e040d, 0xe1fbf2, 0x718155, 0x000000,
  0x3e487e, 0xfffffe, 0x07122f, 0x767416, 0x191414, 0xc4d0d0, 0x0a0f0f, 0x36145c,
  0x000000, 0x419f41, 0x04170d, 0x033d2b, 0x518c73, 0x1f2436, 0xffffff, 0x221d29,
  0x9b0364, 0x4c4664, 0x464b6b, 0xef3251, 0xfefefe, 0xffffff, 0x000000, 0xc8a872,
  0xd9d7cf, 0xf8f8fc, 0x242424, 0xfefeff, 0x562a2a, 0xcacccb, 0xb364a9, 0x0166b2,
  0xfdfdfd, 0xd3d3d3, 0xdddddd, 0xffffff, 0x1a717b, 0xffffff, 0xe7e7e7, 0x172479,
  0x01042b, 0x033d2b, 0x0080e3, 0x232054, 0x005787, 0xe6a9ff, 0x145414, 0x272755,
  0xffffff, 0xd8d7da, 0x002d5c, 0xd89b85, 0xfefefe, 0xc4c4c4, 0xf0f0cd, 0xe2d4b2,
  0x6c192d, 0xbcb8a4, 0x4e3ae7, 0x3492a8, 0xccdee7, 0xaf6829, 0x00137f, 0x5357a0,
  0x47a197, 0xfffc00, 0x000089, 0x000850, 0x000000, 0x000000, 0x61707e, 0xffffff,
  0x480121, 0x010000, 0x9b0394, 0xbefdff, 0xb035f0, 0x09006a, 0x2f4d92, 0x4b4b4b,
  0x4d156a, 0x7fb6ab, 0x000000, 0x4f4951, 0x460000, 0x004646, 0xffad00, 0x7187c0,
  0x3d64d8, 0x73252c, 0x343658, 0x5d5d5d, 0xffec21, 0x86ad91, 0x0f168a, 0x3f567c,
  0x566393, 0xd83d54, 0xdcdbdc, 0x102347, 0x272a2f, 0x285769, 0x324b72, 0x466b61,
  0x598e63, 0xbcbcce, 0xbebe9a, 0x3b3da8, 0x61707e, 0x465c7a, 0x4e7db6, 0x191919,
  0x50005d, 0x000100, 0x17274a, 0x808080, 0xdcdcdc, 0x0d0a25, 0xa1a1a1, 0x888686,
  0x848484, 0xe45e15, 0x102410, 0x353535, 0x182462, 0x32001e, 0x111111, 0x637eb1,
  0xa04141, 0x3b5429, 0xc64e4e, 0x000000, 0xff3429, 0x143450, 0x101010, 0x0d3624,
  0x413931, 0xf6f6f6, 0x4b98ab, 0x11234b, 0x84b694, 0x4e5d96, 0x405a64, 0xffffff,
  0x094545, 0xc06999, 0x000533, 0x669967, 0x000000, 0x5a0000, 0x4d3a24, 0x237596,
  0x06582c, 0x000000, 0x142b55, 0x040f13, 0x0c2444, 0x5e7d8e, 0x234069, 0x9c1d1d,
  0xfb6700, 0x8090a6, 0x442863, 0x003300, 0x000000, 0x00084e, 0x57455f, 0xa8ffff,
  0xa8ffff, 0x00ffff, 0x232941, 0xfcfff1, 0x4d526c, 0x9cad4d, 0x040aff, 0x765f4d,
  0x361c1c, 0x997fa6, 0x008080, 0x0b1035, 0x6e583e, 0x5fb67e, 0x490249, 0x030663,
];

/** MapPanel.cs makeBright: 0.5 + signedSqrt(c − 0.5) per channel. */
function makeBright(rgb: number): string {
  const f = (v: number): number => {
    const c = v / 255 - 0.5;
    const b = 0.5 + (c > 0 ? Math.sqrt(c) : -Math.sqrt(-c));
    return Math.round(Math.max(0, Math.min(1, b)) * 255);
  };
  const r = f((rgb >> 16) & 0xff);
  const g = f((rgb >> 8) & 0xff);
  const b = f(rgb & 0xff);
  return `rgb(${r},${g},${b})`;
}

/** MapPanel.cs markColor: colour of a collectable mark character. */
function markColor(c: string): string {
  switch (c) {
    case 'm': return '#0000ff';
    case 'r': case 'R': return '#ff0000';
    case 'y': case 'Y': return '#bfbf00';
    case 'b': case 'B': return '#0000ff';
    case 'u': case 'U': return '#8000ff';
    case 'k': case 'd': return '#00a600';
    default: return '#ff0000';
  }
}

/** MapPanel.cs markChar: keys collapse to 'K', doors to 'D', else the capitalized char. */
function markChar(c: string): string {
  const mapped = 'rybu'.includes(c) ? 'k' : 'RYBU'.includes(c) ? 'd' : c;
  return mapped.toUpperCase();
}

/**
 * The map screen (M key, MapPanel.cs): a grid minimap of the world, opened
 * centred on the current area at 1:1 cell scale. Arrow keys scroll (600px/s),
 * +/- (or A/D, or the wheel) zoom, dragging pans, and the jump key toggles a
 * blue 'M' user mark on the current area (persisted via Marked). Pinch zoom is
 * not ported.
 */
export class MapPanel {
  private el: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private padRaf = 0;
  private padWasDown = true; // map/pause button likely still held from opening
  private raf = 0;
  private lastT = 0;
  private keys = new Set<string>();
  private jumpWasDown = false;
  private scale = 1;
  private originX = 0; // canvas px of the map's (minBounds) top-left corner
  private originY = 0;
  private dragging = false;
  private closed = false;

  // Per-area extra-data overrides (MapPanel.cs:108-131), keyed by real position.
  private cellColor = new Map<string, string>();
  private cellHidden = new Set<string>();

  constructor(
    private parent: HTMLElement,
    private game: Game,
    private onClose: () => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'map-panel';
    this.canvas = document.createElement('canvas');
    this.el.appendChild(this.canvas);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'map-close';
    closeBtn.textContent = 'X';
    closeBtn.setAttribute('aria-label', 'Close map');
    closeBtn.addEventListener('click', () => this.close());
    this.el.appendChild(closeBtn);
    parent.appendChild(this.el);

    // ShowMap (MapPanel.cs:200): refresh the current area's collectable marks.
    game.objectLayers?.checkCollectables(game.values);

    this.prepareCells();

    // Open centred on the current area at scale 1 (MapPanel.cs:187-196).
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    const cur = game.currentArea.mapPosition;
    const w = game.world;
    this.scale = 1;
    this.originX = this.canvas.width / 2 - ((cur.x - w.minBounds.x) + 0.5) * XSIZE;
    this.originY = this.canvas.height / 2 - ((cur.y - w.minBounds.y) + 0.5) * YSIZE;

    // Drag to pan; wheel to zoom.
    this.canvas.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.canvas.setPointerCapture(e.pointerId);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      this.originX += e.movementX;
      this.originY += e.movementY;
      this.clampPan();
    });
    for (const ev of ['pointerup', 'pointercancel'] as const) {
      this.canvas.addEventListener(ev, () => (this.dragging = false));
    }
    this.el.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoom(e.deltaY > 0 ? 0.9 : 10 / 9);
    }, { passive: false });

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.padRaf = requestAnimationFrame(this.pollPad);
    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  /** Resolve MapVisible / MapColor extra-data for every area (MapPanel.cs init). */
  private prepareCells(): void {
    for (const area of this.game.world.areas.values()) {
      if (!area || area.empty || !area.extraData) continue;
      const key = area.mapPosition.key;
      const vis = area.getExtraData('MapVisible')?.toLowerCase();
      if (vis === 'false') this.cellHidden.add(key);
      const color = parseIniInt(area.getExtraData('MapColor'));
      if (color != null) {
        if (color === 64) this.cellHidden.add(key);
        else {
          // base-4 RGB: r = c%4, g = (c/4)%4, b = (c/16)%4, each /3 of full.
          const ch = (v: number): number => Math.round((v / 3) * 255);
          this.cellColor.set(
            key,
            `rgb(${ch(color % 4)},${ch(Math.floor(color / 4) % 4)},${ch(Math.floor(color / 16) % 4)})`,
          );
        }
      }
    }
  }

  private frame = (t: number): void => {
    const dt = Math.min(0.05, (t - this.lastT) / 1000);
    this.lastT = t;
    // Arrow-key scroll (600px/s, MapPanel.cs:279-284) + A/D and +/- zoom keys.
    if (this.keys.has('ArrowUp')) this.originY += SCROLL_SPEED * dt;
    if (this.keys.has('ArrowDown')) this.originY -= SCROLL_SPEED * dt;
    if (this.keys.has('ArrowLeft')) this.originX += SCROLL_SPEED * dt;
    if (this.keys.has('ArrowRight')) this.originX -= SCROLL_SPEED * dt;
    this.clampPan();
    this.draw();
    this.raf = requestAnimationFrame(this.frame);
  };

  private zoom(k: number): void {
    // Zoom about the canvas centre.
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    this.originX = cx + (this.originX - cx) * k;
    this.originY = cy + (this.originY - cy) * k;
    this.scale *= k;
    this.clampPan();
  }

  private clampPan(): void {
    const w = this.game.world;
    const cols = Math.max(1, w.size.x);
    const rows = Math.max(1, w.size.y);
    const mw = cols * XSIZE * this.scale;
    const mh = rows * YSIZE * this.scale;
    // Keep at least a BORDER of the map on screen in each axis.
    this.originX = Math.min(this.canvas.width - BORDER, Math.max(BORDER - mw, this.originX));
    this.originY = Math.min(this.canvas.height - BORDER, Math.max(BORDER - mh, this.originY));
  }

  private draw(): void {
    const g = this.game;
    const w = g.world;
    const ctx = this.canvas.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    const s = this.scale;
    const cw = XSIZE * s;
    const chh = YSIZE * s;
    const reveal = g.hasMapPower();

    for (const area of w.areas.values()) {
      if (!area || area.empty) continue;
      const mp = area.mapPosition;
      const key = mp.key;
      if (this.cellHidden.has(key)) continue;
      const visited = g.values.visited.has(w.getMapIndex(mp));
      if (!reveal && !visited) continue;

      const px = this.originX + (mp.x - w.minBounds.x) * cw;
      const py = this.originY + (mp.y - w.minBounds.y) * chh;
      if (px + cw < 0 || py + chh < 0 || px > this.canvas.width || py > this.canvas.height)
        continue;

      ctx.fillStyle = visited
        ? this.cellColor.get(key) ??
          (area.background < GRADIENTS.length ? makeBright(GRADIENTS[area.background]) : VISITED_COLOR)
        : NOT_VISITED_COLOR;
      ctx.fillRect(px + s, py + s, cw - 2 * s, chh - 2 * s);

      // Collectable/user marks: up to two coloured characters (MapPanel.cs draw).
      const mark = visited ? g.values.marked.get(key) : undefined;
      if (mark) {
        ctx.font = `bold ${Math.max(8, 10 * s)}px monospace`;
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = markColor(mark[0]);
        ctx.fillText(markChar(mark[0]), px + 4 * s, py + 12 * s);
        if (mark.length === 2) {
          ctx.fillStyle = markColor(mark[1]);
          ctx.fillText(markChar(mark[1]), px + 15 * s, py + 12 * s);
        } else if (mark.length > 2) {
          ctx.fillStyle = '#ff0000';
          ctx.fillText('+', px + 15 * s, py + 12 * s);
        }
      }
    }

    // Current area: red border (MapPanel.cs CURRENT_BORDER).
    const cur = g.currentArea.mapPosition;
    ctx.strokeStyle = CURRENT_BORDER;
    ctx.lineWidth = 2;
    ctx.strokeRect(
      this.originX + (cur.x - w.minBounds.x) * cw,
      this.originY + (cur.y - w.minBounds.y) * chh,
      cw,
      chh,
    );
  }

  /** Jump key: toggle the blue 'M' user mark on the current area (MapPanel.cs mark()). */
  private toggleUserMark(): void {
    const g = this.game;
    const pos = g.currentArea.mapPosition;
    const marked = hasUserMark(g.values, pos);
    // Written to both the live values and the save snapshot's marks
    // (CurrentSave.SourcePowers) — persisted with the next save write.
    const src = new JuniValues();
    g.save.readInto(src);
    if (marked) {
      g.values.unsetMark(pos, Collectable.User);
      src.unsetMark(pos, Collectable.User);
    } else {
      g.values.setMark(pos, Collectable.User);
      src.setMark(pos, Collectable.User);
    }
    g.save.setMarked(src.marked);
  }

  // The game loop is frozen while the map is open, so poll the gamepad
  // map/pause buttons (R1/L1, yknytt's Joy(5)/Joy(4)) here to close.
  private pollPad = (): void => {
    let down = false;
    for (const pad of navigator.getGamepads?.() ?? []) {
      if (pad?.connected && (pad.buttons[5]?.pressed || pad.buttons[4]?.pressed)) down = true;
    }
    if (down && !this.padWasDown) {
      this.close();
      return;
    }
    this.padWasDown = down;
    this.padRaf = requestAnimationFrame(this.pollPad);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'KeyM' || e.code === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.code.startsWith('Arrow')) {
      e.preventDefault();
      this.keys.add(e.code);
      return;
    }
    // Zoom: walk/umbrella (A/D, MapPanel.cs) plus the more discoverable +/-.
    if (e.code === 'KeyA' || e.code === 'Minus' || e.code === 'NumpadSubtract') this.zoom(0.9);
    if (e.code === 'KeyD' || e.code === 'Equal' || e.code === 'NumpadAdd') this.zoom(10 / 9);
    // Jump (S) toggles the user mark, edge-triggered.
    if (e.code === 'KeyS' && !this.jumpWasDown) {
      this.jumpWasDown = true;
      this.toggleUserMark();
    }
    if (e.code === 'KeyS' || e.code === 'KeyA' || e.code === 'KeyD') e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    if (e.code === 'KeyS') this.jumpWasDown = false;
  };

  private close(): void {
    if (this.closed) return;
    this.closed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    cancelAnimationFrame(this.padRaf);
    cancelAnimationFrame(this.raf);
    this.el.remove();
    this.onClose();
  }
}

function hasUserMark(values: JuniValues, pos: KnyttPoint): boolean {
  return (values.marked.get(pos.key) ?? '').includes('m');
}
